import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useReducedMotionSafe } from "@/components/animation/AnimationSettings";
import { PALETTE } from "@/components/animation/MotionShared";
import Logix2Dock from "@/components/Logix2Dock";
import { logix2Dock } from "@/api/adminOneClient";

/**
 * Animated LOGIX-2 Voice Dock wrapper (Phase 30G).
 *
 * Renders the existing `<Logix2Dock />` (preserved entirely) with an added
 * animated avatar + voice waveform pulse + greeting plate that floats above
 * the dock. Speaks a short, compliance-safe daily greeting on demand.
 *
 * Strictly additive — the original dock still works on its own.
 */
export default function Logix2VoiceDock({
  displayName,
  greetOnMount = false,
  testid = "logix2-voice-dock",
}) {
  const reduce = useReducedMotionSafe();
  const [speaking, setSpeaking] = useState(false);
  const greetedRef = useRef(false);

  const greet = useCallback(async () => {
    if (speaking) return;
    const firstName = (displayName || "").trim().split(" ")[0] || "there";
    const text =
      `Hi ${firstName}. I am LOGIX-2, your CareLogix ORACLE assistant. ` +
      `Today I am tracking stocks, crypto, forex, sports, healthcare, startups, and grants. ` +
      `Everything I share is educational research only. Not financial advice. Human decision required.`;
    setSpeaking(true);
    try {
      try {
        const { data } = await logix2Dock.voiceSpeak({ text, provider: "browser", summary_only: true });
        const spoken = data?.spoken_text || text;
        if (data?.provider === "elevenlabs" && data?.audio_b64) {
          const audio = new Audio(`data:${data.audio_mime || "audio/mpeg"};base64,${data.audio_b64}`);
          audio.onended = () => setSpeaking(false);
          audio.play();
          return;
        }
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
          const u = new SpeechSynthesisUtterance(spoken);
          u.rate = data?.rate || 1.0;
          u.pitch = data?.pitch || 1.0;
          u.onend = () => setSpeaking(false);
          window.speechSynthesis.cancel();
          window.speechSynthesis.speak(u);
          return;
        }
      } catch (_) {
        // fallback below
      }
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        const u = new SpeechSynthesisUtterance(text);
        u.onend = () => setSpeaking(false);
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(u);
        return;
      }
      setSpeaking(false);
    } finally {
      setTimeout(() => setSpeaking(false), 60_000);
    }
  }, [displayName, speaking]);

  useEffect(() => {
    if (greetOnMount && !greetedRef.current && displayName) {
      greetedRef.current = true;
      // small delay so the dock has time to mount
      const t = setTimeout(() => { greet(); }, 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [greetOnMount, displayName, greet]);

  return (
    <div style={S.shell} data-testid={testid}>
      {/* Animated greeting plate above the existing dock */}
      <div style={S.plate}>
        <motion.button
          type="button"
          aria-label={speaking ? "LOGIX-2 speaking" : "Greet me"}
          onClick={greet}
          style={S.avatarBtn}
          animate={reduce ? {} : { boxShadow: speaking
            ? ["0 0 0 rgba(197,160,89,0)", "0 0 20px rgba(197,160,89,0.55)", "0 0 0 rgba(197,160,89,0)"]
            : "0 0 12px rgba(197,160,89,0.25)"
          }}
          transition={reduce ? { duration: 0 } : { duration: 1.5, repeat: speaking ? Infinity : 0 }}
          data-testid={`${testid}-avatar`}
        >
          <span aria-hidden="true" style={S.avatarGlyph}>🐕</span>
          <span style={S.avatarLabel}>{speaking ? "● SPEAKING" : "◦ IDLE"}</span>
        </motion.button>
        <div style={S.waveform} aria-hidden="true">
          {[...Array(7)].map((_, i) => (
            <motion.span
              key={i}
              style={{ ...S.bar, height: 6 + (i % 3) * 4 }}
              animate={speaking && !reduce ? { scaleY: [1, 1.6 + (i % 4) * 0.25, 1] } : { scaleY: 1 }}
              transition={reduce ? { duration: 0 } : { duration: 0.6 + i * 0.05, repeat: speaking ? Infinity : 0, ease: "easeInOut" }}
            />
          ))}
        </div>
      </div>
      {/* Preserved existing dock (no changes) */}
      <Logix2Dock />
    </div>
  );
}

const S = {
  shell: { position: "fixed", right: 16, bottom: 92, zIndex: 60, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, pointerEvents: "none" },
  plate: { display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: PALETTE.glassDark, border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 999, pointerEvents: "auto", boxShadow: "0 6px 22px rgba(0,0,0,0.45)" },
  avatarBtn: { display: "flex", alignItems: "center", gap: 6, appearance: "none", border: `1px solid ${PALETTE.goldGlow}`, background: "rgba(197,160,89,0.10)", color: PALETTE.gold, padding: "4px 9px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.10em" },
  avatarGlyph: { fontSize: 16, filter: "saturate(1.2)" },
  avatarLabel: { color: PALETTE.gold },
  waveform: { display: "inline-flex", alignItems: "center", gap: 3 },
  bar: { width: 3, borderRadius: 2, background: PALETTE.gold, transformOrigin: "50% 50%" },
};
